import type { Prng } from '../rng.js';

/**
 * Top-K + softmax sampling for AI choice diversification.
 *
 * Pure functions; no game-state knowledge. Given a list of scored
 * candidates and a temperature, returns a probability distribution
 * (or a sampled pick when paired with a Prng). Personality.randomness
 * feeds in as the temperature: low (Cautious 0.05) ≈ deterministic,
 * high (Wildcard 0.45) ≈ noticeable variance among the top-K.
 *
 * Why softmax instead of uniform-over-top-K: softmax respects the
 * score gap. If candidate A scores +0.8 and B scores +0.1, even at
 * Wildcard temperature A is still strongly preferred; B is a "huh,
 * sometimes" pick. Uniform sampling would equalize them, which is
 * too random.
 */

export interface Scored<T> {
  readonly item: T;
  readonly score: number;
}

/**
 * Sort `scored` by descending score and take the top `k`. Returns a
 * fresh array — does not mutate the input. Behavior on ties: stable
 * sort keeps original order, so deterministic given the input.
 */
export function pickTopK<T>(
  scored: readonly Scored<T>[],
  k: number
): readonly Scored<T>[] {
  if (k <= 0 || scored.length === 0) return [];
  const sorted = [...scored].sort((a, b) => b.score - a.score);
  return sorted.slice(0, Math.min(k, sorted.length));
}

/**
 * Compute softmax weights from raw scores. `temperature` scales the
 * spread:
 *  - temperature ≤ 0 → deterministic. The maximum-scoring entries
 *    split the weight equally; everyone else gets 0.
 *  - temperature ≈ 0.05 → very peaked at the top.
 *  - temperature ≈ 0.5  → distinct top-3 still favor the leader by
 *    a clear margin but the second-place sees real probability mass.
 *
 * Subtracts max before exp() for numerical stability — exp of large
 * positives overflows, exp of large negatives underflows; the max-
 * shift keeps everything in a sane range.
 */
export function softmaxWeights(
  scores: readonly number[],
  temperature: number
): readonly number[] {
  if (scores.length === 0) return [];
  if (temperature <= 0) {
    let max = Number.NEGATIVE_INFINITY;
    for (const s of scores) if (s > max) max = s;
    const winners = scores.map((s) => s === max);
    const count = winners.filter(Boolean).length;
    return winners.map((w) => (w ? 1 / count : 0));
  }
  let max = Number.NEGATIVE_INFINITY;
  for (const s of scores) if (s > max) max = s;
  const exps = scores.map((s) => Math.exp((s - max) / temperature));
  let sum = 0;
  for (const e of exps) sum += e;
  if (sum === 0) {
    // Pathological — all -Infinity. Fall back to uniform.
    return scores.map(() => 1 / scores.length);
  }
  return exps.map((e) => e / sum);
}

/**
 * Sample one item from `scored` using softmax probabilities. The Prng
 * draws a single uniform [0,1) and walks the cumulative distribution.
 * Returns the last item if floating-point drift sends the pointer
 * past 1.0 — robustness, not normal flow.
 */
export function softmaxSample<T>(
  scored: readonly Scored<T>[],
  temperature: number,
  prng: Prng
): T {
  if (scored.length === 0) {
    throw new Error('softmaxSample: empty input');
  }
  if (scored.length === 1) return scored[0]!.item;
  const weights = softmaxWeights(
    scored.map((s) => s.score),
    temperature
  );
  const r = prng.next();
  let acc = 0;
  for (let i = 0; i < scored.length - 1; i++) {
    acc += weights[i]!;
    if (r < acc) return scored[i]!.item;
  }
  return scored[scored.length - 1]!.item;
}
